import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState, type JSX } from "react";

import { paletteIn, scrimFade } from "../lib/motion";

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly hint?: string;
  readonly keywords?: string;
  run: () => void;
}

const HISTORY_KEY = "voss-cmd-history";
const readHistory = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};
const pushHistory = (id: string): void => {
  const next = [id, ...readHistory().filter((h) => h !== id)].slice(0, 8);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
};

/** ⌘K command palette: run commands, jump workspaces, and search conversations with keyboard nav. */
export function CommandPalette({ open, commands, onClose }: { open: boolean; commands: readonly Command[]; onClose: () => void }): JSX.Element {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      inputRef.current?.focus();
    }
  }, [open]);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const items = commands.filter((c) => `${c.label} ${c.group} ${c.keywords ?? ""}`.toLowerCase().includes(q));
      return [{ title: "Results", items }];
    }
    const history = readHistory();
    const recent = history.map((id) => commands.find((c) => c.id === id)).filter((c): c is Command => Boolean(c)).slice(0, 5);
    const recentIds = new Set(recent.map((c) => c.id));
    const rest = commands.filter((c) => !recentIds.has(c.id));
    return [...(recent.length ? [{ title: "Recent", items: recent }] : []), { title: "All", items: rest }];
  }, [commands, query]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  useEffect(() => setSelected(0), [query]);

  const run = (cmd: Command): void => {
    pushHistory(cmd.id);
    onClose();
    cmd.run();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = flat[selected];
      if (cmd) run(cmd);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  let running = -1;
  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="voss-scrim" variants={scrimFade} initial="hidden" animate="visible" exit="exit" onClick={onClose} style={{ alignItems: "flex-start" }}>
          <motion.div
            className="voss-palette"
            variants={paletteIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onKeyDown}
          >
            <input ref={inputRef} className="voss-palette__input" placeholder="Search commands, workspaces, conversations…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Command palette search" />
            <div className="voss-palette__list" role="listbox">
              {flat.length === 0 ? <div className="voss-empty">No matches</div> : null}
              {sections.map((section) =>
                section.items.length === 0 ? null : (
                  <div key={section.title}>
                    <div className="voss-palette__group">{section.title}</div>
                    {section.items.map((cmd) => {
                      running += 1;
                      const idx = running;
                      return (
                        <button key={`${section.title}-${cmd.id}`} type="button" role="option" aria-selected={idx === selected} className="voss-palette__item" data-active={idx === selected} onMouseEnter={() => setSelected(idx)} onClick={() => run(cmd)}>
                          <span className="voss-palette__label">{cmd.label}</span>
                          <span className="voss-palette__hint">{cmd.hint ?? cmd.group}</span>
                        </button>
                      );
                    })}
                  </div>
                ),
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
